import React, { useState, useEffect } from 'react';
import { Save, Upload, User, Award, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminFirmas() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [config, setConfig] = useState({
    presidente_nombre: '',
    presidente_cargo: 'Presidente/a',
    presidente_firma_url: '',
    instructor_nombre: '',
    instructor_titulo: '',
    instructor_firma_url: '',
    usar_firma_instructor: false
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/config/firmas`);
      if (res.data) {
        setConfig(prev => ({ ...prev, ...res.data }));
      }
    } catch (err) {
      console.error('Error cargando config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccess(false);
    try {
      await axios.put(`${API_URL}/admin/config/firmas`, config);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error guardando:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (field, file) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(`${API_URL}/admin/upload-firma`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setConfig(prev => ({ ...prev, [field]: res.data.url }));
    } catch (err) {
      console.error('Error subiendo imagen:', err);
      alert('Error al subir la imagen');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Configuración de Firmas</h1>
        <p className="text-gray-600">Configurá las firmas que aparecerán en constancias y certificados</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Firma Presidente */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Firma del Presidente/a
            </CardTitle>
            <CardDescription>
              Esta firma aparece siempre a la derecha en los documentos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="presidente_nombre">Nombre *</Label>
              <Input
                id="presidente_nombre"
                value={config.presidente_nombre}
                onChange={(e) => setConfig(prev => ({ ...prev, presidente_nombre: e.target.value }))}
                placeholder="Ej: Lic. María González"
              />
            </div>

            <div>
              <Label htmlFor="presidente_cargo">Cargo / Autoridad *</Label>
              <Input
                id="presidente_cargo"
                value={config.presidente_cargo}
                onChange={(e) => setConfig(prev => ({ ...prev, presidente_cargo: e.target.value }))}
                placeholder="Ej: Presidente Colegio TS Distrito Lomas"
              />
              <p className="text-xs text-gray-500 mt-1">
                Este cargo aparecerá debajo de la firma en los certificados
              </p>
            </div>
            
            <div>
              <Label>Imagen de la firma</Label>
              <div className="mt-2 space-y-3">
                {config.presidente_firma_url && (
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <img 
                      src={config.presidente_firma_url} 
                      alt="Firma presidente" 
                      className="max-h-24 mx-auto"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full mt-2 text-red-600"
                      onClick={() => setConfig(prev => ({ ...prev, presidente_firma_url: '' }))}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Eliminar imagen
                    </Button>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('presidente_firma_url', e.target.files[0])}
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  O ingresá una URL directamente:
                </p>
                <Input
                  value={config.presidente_firma_url}
                  onChange={(e) => setConfig(prev => ({ ...prev, presidente_firma_url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Firma Instructor */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5" />
              Firma del Instructor/a (Opcional)
            </CardTitle>
            <CardDescription>
              Si está habilitada, aparece a la izquierda en los documentos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="usar_firma_instructor"
                checked={config.usar_firma_instructor}
                onChange={(e) => setConfig(prev => ({ ...prev, usar_firma_instructor: e.target.checked }))}
                className="rounded"
              />
              <Label htmlFor="usar_firma_instructor" className="cursor-pointer">
                Incluir firma de instructor en los documentos
              </Label>
            </div>

            {config.usar_firma_instructor && (
              <>
                <div>
                  <Label htmlFor="instructor_nombre">Nombre del Instructor/a</Label>
                  <Input
                    id="instructor_nombre"
                    value={config.instructor_nombre}
                    onChange={(e) => setConfig(prev => ({ ...prev, instructor_nombre: e.target.value }))}
                    placeholder="Ej: Lic. Juan Pérez"
                  />
                </div>

                <div>
                  <Label htmlFor="instructor_titulo">Título / Cargo</Label>
                  <Textarea
                    id="instructor_titulo"
                    value={config.instructor_titulo}
                    onChange={(e) => setConfig(prev => ({ ...prev, instructor_titulo: e.target.value }))}
                    placeholder={"Ej: Presidente\nColegio de Trabajadorxs Sociales"}
                    rows={2}
                    maxLength={120}
                    className="resize-none"
                    data-testid="instructor-titulo-textarea"
                  />
                  <p className="text-xs text-gray-500 mt-1">Podés usar hasta 2 líneas (Enter para saltar de línea).</p>
                </div>
                
                <div>
                  <Label>Imagen de la firma</Label>
                  <div className="mt-2 space-y-3">
                    {config.instructor_firma_url && (
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <img 
                          src={config.instructor_firma_url} 
                          alt="Firma instructor" 
                          className="max-h-24 mx-auto"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full mt-2 text-red-600"
                          onClick={() => setConfig(prev => ({ ...prev, instructor_firma_url: '' }))}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Eliminar imagen
                        </Button>
                      </div>
                    )}
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload('instructor_firma_url', e.target.files[0])}
                    />
                    <p className="text-xs text-gray-500">O URL directa:</p>
                    <Input
                      value={config.instructor_firma_url}
                      onChange={(e) => setConfig(prev => ({ ...prev, instructor_firma_url: e.target.value }))}
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Preview */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Vista previa</CardTitle>
          <CardDescription>Así se verán las firmas en los documentos</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg p-6 bg-white">
            <div className="flex justify-between items-end">
              {config.usar_firma_instructor && (
                <div className="text-center">
                  {config.instructor_firma_url ? (
                    <img src={config.instructor_firma_url} alt="" className="h-16 mx-auto mb-2" />
                  ) : (
                    <div className="h-16 border-b border-gray-400 w-40 mb-2"></div>
                  )}
                  <p className="font-medium text-sm">{config.instructor_nombre || '________________'}</p>
                  <p className="text-xs text-gray-500">{config.instructor_titulo || 'Instructor/a'}</p>
                </div>
              )}
              <div className={`text-center ${!config.usar_firma_instructor ? 'ml-auto' : ''}`}>
                {config.presidente_firma_url ? (
                  <img src={config.presidente_firma_url} alt="" className="h-16 mx-auto mb-2" />
                ) : (
                  <div className="h-16 border-b border-gray-400 w-40 mb-2"></div>
                )}
                <p className="font-medium text-sm">{config.presidente_nombre || '________________'}</p>
                <p className="text-xs text-gray-500">{config.presidente_cargo || 'Presidente/a'}</p>
                <p className="text-xs text-gray-500">Colegio Profesional de Trabajadorxs Sociales</p>
                <p className="text-xs text-gray-500">Distrito Lomas de Zamora</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="mt-6 flex items-center gap-4">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Guardando...' : 'Guardar configuración'}
        </Button>
        {success && (
          <span className="text-emerald-600 font-medium">✓ Guardado correctamente</span>
        )}
      </div>
    </div>
  );
}
